import React, { useEffect, useRef, useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import "./Chat.css";
import "../docfinder/DocFinder.css"; // Import for doc-finder-container styles
import { v4 as uuidv4 } from "uuid";
import { knowledgeGraphChat } from "../../services/AxiosServices";
import RenderMessage from "../../components/message/RenderMessage";
import MessageFooter from "../../components/message/MessageFooter";
import { useMyContext } from "../../context/DataContext";
import KgSidebar from "./KgSidebar";
import DocLinkSidebar from "./DocLinkSidebar";
import { useAuth0 } from "@auth0/auth0-react";
import { logError } from "../../utils/errorHandling";


interface Message {
    user_id?: string;
    text?: string;
    thread_ts?: string | null;
    senderType: string;
    id?: string;
    assignedId?: string;
    keyPosition: number;
    visualizationData?: { [key: string]: any } | null;
    fileData: any;
    source: any;
    graph_data?: any;
    isDownloadable?: boolean;
}

let currentUsername: string | Blob = "";
let currentUUID: string | Blob = "";

const KGChat: React.FC = () => {
    const { state, updateState } = useMyContext();
    useEffect(() => {
        const originalPosition = document.body.style.position;
        document.body.style.position = 'fixed';
        return () => {
            document.body.style.position = originalPosition;
            updateState("isKgSidebarOpen", false);
            updateState("kgSidebarData", null);
            updateState("isDocLinkSidebarOpen", false);
            updateState("docLinkSidebarData", null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let lastSender: string | null = null;
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState<string>("");
    const [isRole, setIsRole] = useState<string>("user");
    const [showChat, setShowChat] = useState<boolean>(false);
    const [typingLoader, setTypingLoader] = useState<boolean>(false);
    const chatRef = useRef<HTMLTextAreaElement>(null);
    const companyLogo = "/chaticonlogo.png";
    const { user, getAccessTokenSilently } = useAuth0();

    const username = (user?.preferred_username) || "Moonjelly User";

    React.useEffect(() => {
        const newUuid = uuidv4();
        if (username !== currentUsername) {
            currentUsername = username;
            currentUUID = newUuid;
        }
    }, [username]);

    const handleSendMessage = async (senderType: string) => {
        const actionId = uuidv4();
        setShowChat(true);
        setTypingLoader(true);
        if (newMessage.trim()) {
            setIsRole("user");
            //@ts-ignore
            const userMessage: Message = {
                id: actionId,
                text: newMessage,
                senderType
            };
            setMessages((prevMessages) => [...prevMessages, userMessage]);
            setNewMessage("");
        }

        let payLoad = JSON.stringify({
            user_id: currentUsername,
            text: newMessage,
            channel_id: currentUUID,
            thread_ts: currentUUID,
            chat_provider_name: "INTERNAL_UI",
            ts: actionId,
        });

        try {
            const chatResponse = async () => {
                const token = await getAccessTokenSilently();
                return (await knowledgeGraphChat(payLoad, token)) as any;
            };
            const result = await chatResponse();

            let text = "";
            let source = result?.source_reference || null;
            const respMsg = result?.response_message;

            if (typeof respMsg === 'string') {
                text = respMsg;
            } else if (respMsg && typeof respMsg === 'object') {
                text = respMsg.answer || "";
                source = respMsg.source || source;
            }

            const msg: Message = {
                text: text,
                senderType: "admin",
                user_id: username,
                keyPosition: 0,
                fileData: result?.fileData || null,
                source: source,
                graph_data: result?.graph_data || null,
            };

            setMessages((prevMessages) => [...prevMessages, msg]);
            setShowChat(true);
            if (result) {
                if (chatRef.current) {
                    chatRef.current.focus();
                }
            }
        } catch (error) {
            logError("Knowledge graph chat failed", error);
        } finally {
            setTypingLoader(false);
        }

    };

    const handleKeyPressSend = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(isRole);
        }
    };

    useEffect(() => {
        if (typingLoader === false) {
            if (chatRef.current) {
                chatRef.current.focus();
            }
        }
    }, [typingLoader]);

    const inputHandler = (e) => {
        setNewMessage(e.target.value);
        if (state.isKgSidebarOpen) {
            updateState("isKgSidebarOpen", false);
        }
    };

    const handleAddToChat = (docName: string) => {
        setNewMessage(docName);
        if (chatRef.current) {
            chatRef.current.focus();
        }
    };

    useEffect(() => {
        if (chatRef.current) {
            autoExpand({ target: chatRef.current });
        }
    }, [newMessage]);

    const autoExpand = (event) => {
        const target = event.target;
        target.style.height = "auto";
        target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
    };

    const [previousHeight, setPreviousHeight] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const chatBoxRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }

        if (chatBoxRef.current) {
            const currentHeight = chatBoxRef.current.scrollHeight;
            if (currentHeight > previousHeight) {
                setPreviousHeight(currentHeight);
            }
        }
    }, [messages, previousHeight]);

    const filteredMsg = messages.filter((item) => item.text !== undefined);
    let previousId: string | null = null;
    const firstItem = filteredMsg[1] || {};
    const initialThreadTs = firstItem.thread_ts || null;
    const initialUserId = firstItem.user_id || null;

    const transformedData = filteredMsg.map((item) => {
        if (item.id) {
            previousId = item.id;
            return { ...item, id: item.id };
        }
        if (previousId) {
            return { ...item, id: previousId, assignedId: previousId };
        }
        return item;
    });

    let keyPosition = 0;
    let currentId: string | null | undefined = null;

    const dataKeyGet = transformedData.map((item, index, arr) => {
        const itemId = item.id || item.assignedId;

        if (itemId !== currentId) {
            currentId = itemId;
        }
        const threadTs = initialThreadTs;
        const userId = initialUserId;

        if (
            item.senderType === "admin" &&
            (index === arr.length - 1 ||
                (arr[index + 1]?.id || arr[index + 1]?.assignedId) !== currentId)
        ) {
            return {
                ...item,
                id: item.id || previousId,
                keyPosition,
                thread_ts: threadTs,
                user_id: userId,
            };
        }
        const {
            thread_ts,
            user_id,
            ...rest
        } = item;
        return {
            ...rest,
            id: item.id || previousId,
            thread_ts: threadTs,
            user_id: userId,
        };
    });
    

    if (!showChat) {
        return (
            <div className="chat-page-layout">
                <div 
                    className="chat-container doc-finder-container initial-mode"
                    onClick={() => {
                        if (state.isKgSidebarOpen) {
                            updateState("isKgSidebarOpen", false);
                        }
                        if (state.isDocLinkSidebarOpen) {
                            updateState("isDocLinkSidebarOpen", false);
                        }
                    }}
                >
                    <div className="hero-section">
                        <div className="hero-logo">
                            <img src={companyLogo} alt="Loading..." className="loader-logo" />
                        </div>
                        <h1>How can I help you today?</h1>
                    </div>

                    <div className="search-container">
                        <div className={`search-input-wrapper outline-active`}>
                            <textarea
                                value={newMessage}
                                onChange={(e) => {
                                    inputHandler(e);
                                    autoExpand(e);
                                }}
                                onKeyDown={handleKeyPressSend}
                                placeholder="Type response"
                                ref={chatRef}
                                disabled={typingLoader}
                                rows={1}
                                className="search-textarea"
                            />
                            <button
                                onClick={() => handleSendMessage(isRole)}
                                className={`send-button ${newMessage.trim() && !typingLoader ? 'active' : ''}`}
                                disabled={typingLoader || !newMessage.trim()}
                            >
                                <FaArrowRight />
                            </button>
                        </div>
                    </div>
                </div>
                <KgSidebar 
                    isOpen={state.isKgSidebarOpen} 
                    onClose={() => updateState("isKgSidebarOpen", false)} 
                    documents={state.kgSidebarData || []} 
                    onAddToChat={handleAddToChat}
                />
                <DocLinkSidebar 
                    isOpen={state.isDocLinkSidebarOpen} 
                    onClose={() => updateState("isDocLinkSidebarOpen", false)} 
                    documents={state.docLinkSidebarData || []} 
                    onAddToChat={handleAddToChat}
                />
            </div>
        );
    }

    return (
        <div className="chat-page-layout">
            <div 
                className="chat-container"
                onClick={() => {
                if (state.isKgSidebarOpen) {
                    updateState("isKgSidebarOpen", false);
                }
                if (state.isDocLinkSidebarOpen) {
                    updateState("isDocLinkSidebarOpen", false);
                }
            }}
        >
            <div className={previousHeight > 400 ? "dynamic-chat-boxes" : "chat-box"} ref={chatBoxRef}>
                {showChat && (
                    <div className="chat-box-body">
                        {dataKeyGet.length > 0 &&
                            dataKeyGet.map((message, index) => {
                                const isAdmin = message.senderType === "admin";
                                const showProfilePic = isAdmin && lastSender !== "admin"; // Only show profile pic for the first admin message

                                // Update last sender
                                lastSender = isAdmin ? "admin" : "user";
                                return (
                                    <div
                                        key={index}
                                        className={`chat-message ${message.senderType === "admin"
                                            ? "admin-message"
                                            : "user-message"
                                            }`}
                                    >
                                        {showProfilePic ? (
                                            <img
                                                src={companyLogo}
                                                alt="Admin Logo"
                                                className="message-logo admin-logo"
                                            />
                                        ) : null}
                                        <div
                                            className="message-text admin-bubble"
                                        >
                                            {message.text &&
                                                <p>
                                                    <div className="messageDiv hello">
                                                        <RenderMessage message={message as Message} />
                                                        {
                                                            message.keyPosition === 0 ? <MessageFooter message={message} isKgraph={true} isKgChat />

                                                                : null}
                                                    </div>
                                                </p>
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        {typingLoader ? (
                            <>
                                <div className="typing-indicator">
                                    <img src="/chaticonlogo.png" alt="Logo" className="logo logomr" />
                                </div>
                            </>
                        ) : null}
                        <div ref={messagesEndRef} />
                    </div>
                )}

            </div>
            <div className="chat-box-footer" style={{ padding: "0 10px", bottom: "30px" }}>
                <div className={`search-input-wrapper outline-active`}>
                    <textarea
                        value={newMessage}
                        onChange={(e) => {
                            inputHandler(e);
                            autoExpand(e); // Call autoExpand on change
                        }}
                        onKeyDown={handleKeyPressSend}
                        placeholder={"Type response"}
                        ref={chatRef}
                        disabled={typingLoader} // Ensure this class is applied
                        rows={1}
                        className="search-textarea"
                    />
                    <button
                        onClick={() => handleSendMessage(isRole)}
                        className={`send-button ${newMessage.trim() && !typingLoader ? 'active' : ''}`}
                        disabled={typingLoader || !newMessage.trim()}
                    >
                        <FaArrowRight />
                    </button>
                </div>
            </div>
            </div>
            <KgSidebar 
                isOpen={state.isKgSidebarOpen} 
                onClose={() => updateState("isKgSidebarOpen", false)} 
                documents={state.kgSidebarData || []} 
                onAddToChat={handleAddToChat}
            />
            <DocLinkSidebar 
                isOpen={state.isDocLinkSidebarOpen} 
                onClose={() => updateState("isDocLinkSidebarOpen", false)} 
                documents={state.docLinkSidebarData || []} 
                onAddToChat={handleAddToChat}
            />
        </div>
    );
};

export default KGChat;